import React, { useMemo, useState } from "react";

import {
  AUTH_COPY,
  AUTH_ERROR,
  MAXIMUM_EMAIL_LENGTH,
  MAXIMUM_NAME_LENGTH,
  MAXIMUM_PASSWORD_LENGTH,
  SEX_OPTIONS,
} from "./authenticationConstants";
import {
  composeDateOfBirth,
  validateRegistration,
} from "./authenticationPayload";
import {
  formatUsPhoneInput,
  getUsPhoneState,
  normalizeUsPhone,
} from "../../../services/usPhoneNumber";
import "./treatmentAuthentication.css";
import { US_FLAG_SRC } from "../../../constants/usFlag";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Native account and demographic fields for a new patient. */
export function NewPatientRegistrationStep({
  email,
  pending = false,
  error = null,
  fieldErrors = null,
  brandName = "WellieMD",
  onSubmit,
  onUseExistingAccount,
}) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    month: "",
    day: "",
    year: "",
    sex: "",
    email,
    phone: "",
    password: "",
    confirmPassword: "",
    acknowledged: false,
  });
  const [errors, setErrors] = useState({});
  const [phoneTouched, setPhoneTouched] = useState(false);

  const dateOfBirth = useMemo(
    () => composeDateOfBirth({ month: form.month, day: form.day, year: form.year }),
    [form.month, form.day, form.year]
  );

  const setField = (name) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const setNumericField = (name, maxLength) => (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, maxLength);
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pending) return;
    const candidate = { ...form, dateOfBirth };
    const found = validateRegistration(candidate);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    onSubmit(candidate);
  };

  const errorFor = (name) => errors[name] || fieldErrors?.[name] || null;
  const phoneState = getUsPhoneState(form.phone);
  const phoneError = errorFor("phone");
  const showPhoneError = Boolean(
    phoneError
    || phoneState.status === "invalid"
    || (phoneTouched && phoneState.status === "incomplete")
  );
  const phoneMessage = phoneError
    || (
      phoneState.status === "incomplete"
        ? phoneTouched
          ? "Please enter all 10 digits of your US phone number."
          : `${phoneState.remaining} digit${phoneState.remaining === 1 ? "" : "s"} remaining.`
        : phoneState.status === "invalid"
          ? "Please enter a valid US phone number, including area code."
          : phoneState.status === "valid"
            ? "Valid phone number."
            : null
    );

  const field = (name, label, inputProps = {}) => {
    const message = errorFor(name);
    return (
      <div className="treatment-auth__field">
        <label className="treatment-auth__label" htmlFor={`intake-${name}`}>
          {label}
          <span className="treatment-auth__required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={`intake-${name}`}
          name={name}
          className={[
            "treatment-auth__input",
            message ? "treatment-auth__input--error" : "",
            inputProps.readOnly ? "treatment-auth__input--readonly" : "",
          ].filter(Boolean).join(" ")}
          value={form[name]}
          onChange={setField(name)}
          aria-invalid={Boolean(message)}
          aria-describedby={message ? `intake-${name}-error` : undefined}
          disabled={pending}
          {...inputProps}
        />
        {message && (
          <p className="treatment-auth__error" id={`intake-${name}-error`} role="alert">
            {message}
          </p>
        )}
      </div>
    );
  };

  return (
    <form
      className="treatment-auth treatment-auth--registration"
      onSubmit={handleSubmit}
      noValidate
    >
      <p className="treatment-auth__eyebrow">
        {AUTH_COPY.registrationStep.eyebrow}
      </p>
      <h1 className="treatment-auth__title">
        {AUTH_COPY.registrationStep.title}
      </h1>

      <div className="treatment-auth__row">
        {field("firstName", AUTH_COPY.registrationStep.firstNameLabel, {
          autoComplete: "given-name",
          placeholder: "John",
          maxLength: MAXIMUM_NAME_LENGTH,
        })}
        {field("lastName", AUTH_COPY.registrationStep.lastNameLabel, {
          autoComplete: "family-name",
          placeholder: "Smith",
          maxLength: MAXIMUM_NAME_LENGTH,
        })}
      </div>

      <fieldset className="treatment-auth__fieldset">
        <legend className="treatment-auth__label">
          {AUTH_COPY.registrationStep.dobLabel}
          <span className="treatment-auth__required" aria-hidden="true">
            *
          </span>
        </legend>
        <div className="treatment-auth__row treatment-auth__row--dob">
          <select
            aria-label="Month"
            className="treatment-auth__input"
            value={form.month}
            onChange={setField("month")}
            disabled={pending}
          >
            <option value="">Month</option>
            {MONTHS.map((label, index) => (
              <option key={label} value={index + 1}>
                {label}
              </option>
            ))}
          </select>
          <input
            aria-label="Day"
            className="treatment-auth__input"
            inputMode="numeric"
            maxLength={2}
            placeholder="DD"
            value={form.day}
            onChange={setNumericField("day", 2)}
            disabled={pending}
          />
          <input
            aria-label="Year"
            className="treatment-auth__input"
            inputMode="numeric"
            maxLength={4}
            placeholder="YYYY"
            value={form.year}
            onChange={setNumericField("year", 4)}
            disabled={pending}
          />
        </div>
        {errorFor("dateOfBirth") ? (
          <p className="treatment-auth__error" role="alert">
            {errorFor("dateOfBirth")}
          </p>
        ) : (
          <p className="treatment-auth__note">
            {AUTH_COPY.registrationStep.dobHelp}
          </p>
        )}
      </fieldset>

      <fieldset className="treatment-auth__fieldset">
        <legend className="treatment-auth__label">
          {AUTH_COPY.registrationStep.sexLabel}
          <span className="treatment-auth__required" aria-hidden="true">
            *
          </span>
        </legend>
        <div className="treatment-auth__row treatment-auth__row--choices">
          {SEX_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`treatment-auth__choice${form.sex === option.value ? " treatment-auth__choice--selected" : ""}`}
            >
              <input
                type="radio"
                name="sex"
                value={option.value}
                checked={form.sex === option.value}
                onChange={setField("sex")}
                disabled={pending}
              />
              {option.label}
            </label>
          ))}
        </div>
        {errorFor("sex") ? (
          <p className="treatment-auth__error" role="alert">
            {errorFor("sex")}
          </p>
        ) : (
          <p className="treatment-auth__note">
            {AUTH_COPY.registrationStep.sexHelp}
          </p>
        )}
      </fieldset>

      <div className="treatment-auth__row">
        {field("email", AUTH_COPY.registrationStep.emailLabel, {
          type: "email",
          autoComplete: "email",
          placeholder: "you@example.com",
          readOnly: true,
          maxLength: MAXIMUM_EMAIL_LENGTH,
        })}
        <div className="treatment-auth__field">
          <label className="treatment-auth__label" htmlFor="intake-phone">
            {AUTH_COPY.registrationStep.phoneLabel}
            <span className="treatment-auth__required" aria-hidden="true">
              *
            </span>
          </label>
          <div
            className={`treatment-auth__phone${showPhoneError ? " treatment-auth__phone--error" : ""}`}
          >
            <span className="treatment-auth__phone-prefix" aria-label="United States, country code plus one">
              <img src={US_FLAG_SRC} alt="" aria-hidden="true" width="22" height="14" />
              <span>+1</span>
            </span>
            <input
              id="intake-phone"
              name="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              className="treatment-auth__phone-input"
              placeholder="(202) 555-0123"
              value={formatUsPhoneInput(form.phone)}
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  phone: normalizeUsPhone(event.target.value),
                }));
                setPhoneTouched(false);
              }}
              onBlur={() => setPhoneTouched(true)}
              maxLength={16}
              disabled={pending}
              aria-invalid={showPhoneError}
              aria-describedby={phoneMessage ? "intake-phone-message" : undefined}
            />
          </div>
          {phoneMessage && (
            <p
              id="intake-phone-message"
              className={
                showPhoneError
                  ? "treatment-auth__error"
                  : phoneState.status === "valid"
                    ? "treatment-auth__success"
                    : "treatment-auth__note"
              }
              role={showPhoneError ? "alert" : undefined}
            >
              {phoneMessage}
            </p>
          )}
        </div>
      </div>

      <div className="treatment-auth__row">
        {field("password", "Password", {
          type: "password",
          autoComplete: "new-password",
          placeholder: "At least 8 characters",
          maxLength: MAXIMUM_PASSWORD_LENGTH,
        })}
        {field("confirmPassword", "Confirm Password", {
          type: "password",
          autoComplete: "new-password",
          placeholder: "Enter password again",
          maxLength: MAXIMUM_PASSWORD_LENGTH,
        })}
      </div>

      <label className="treatment-auth__consent">
        <input
          type="checkbox"
          checked={form.acknowledged}
          onChange={setField("acknowledged")}
          disabled={pending}
          aria-invalid={Boolean(errorFor("acknowledged"))}
        />
        <span>
          I agree to{" "}
          <a
            href="/policy/terms_of_service"
            target="_blank"
            rel="noopener noreferrer"
          >
            {brandName}&apos;s Terms
          </a>{" "}
          and{" "}
          <a
            href="/policy/privacy_policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>{" "}
          and consent to{" "}
          <a
            href="/policy/consent_telehealth"
            target="_blank"
            rel="noopener noreferrer"
          >
            Telehealth
          </a>
        </span>
      </label>
      {errorFor("acknowledged") && (
        <p className="treatment-auth__error" role="alert">
          {errorFor("acknowledged")}
        </p>
      )}
      {error && !fieldErrors && (
        <p className="treatment-auth__error" role="alert">
          {error === AUTH_ERROR.accountCreateUnavailable
            ? AUTH_COPY.errors.accountCreateUnavailable
            : error === AUTH_ERROR.identityConflict
              ? AUTH_COPY.errors.identityConflict
              : AUTH_COPY.errors.accountCreateUnavailable}
        </p>
      )}

      <button type="submit" className="treatment-auth__submit" disabled={pending}>
        {pending
          ? AUTH_COPY.registrationStep.submitting
          : AUTH_COPY.registrationStep.submit}
      </button>

      <p className="treatment-auth__footnote">
        {AUTH_COPY.registrationStep.existingPrompt}{" "}
        <button
          type="button"
          className="treatment-auth__link treatment-auth__link--strong"
          onClick={onUseExistingAccount}
          disabled={pending}
        >
          {AUTH_COPY.registrationStep.existingAction}
        </button>
      </p>
    </form>
  );
}

export default NewPatientRegistrationStep;
